import { useState, useEffect, useRef } from "react";
import axios from "axios";
import css from "./index.module.css";
import { useLocation } from "react-router-dom";

import SEO from "@components/seo";
import MainContent from "@components/content";
import Order from "@components/order";
import * as Types from "../../types";

import Tabs from "./menu";
import { toast } from "sonner";
import useUserData from "@hooks/useUserData";
import usePush from "@hooks/usePush";

interface AddressSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    postal_code?: string;
    country: string;
    region_with_type: string;
    city_with_type?: string;
    settlement_with_type?: string;
    street_with_type?: string;
    house?: string;
    flat?: string;
    geo_lat?: string;
    geo_lon?: string;
  };
}

export default function Account() {
  const { userData, refreshUserData, logout } = useUserData();
  const location = useLocation();
  const [page, setPage] = useState((location.state as any)?.page || 0);
  const [orders, setOrders] = useState<Types.Order[]>([]);
  const [addresses, setAddresses] = useState<any>([]);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedAddressSuggestion, setSelectedAddressSuggestion] =
    useState<AddressSuggestion | null>(null);
  const addressDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastRequestedQuery = useRef<string>("");
  const addressInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const { subscribe, isSubscribed, isApp, isLoading, error } = usePush();

  useEffect(() => {
    if (isSubscribed) console.log("Устройство подписано!");
  }, [isSubscribed]);

  useEffect(() => {
    axios
      .get(`${import.meta.env.VITE_API_URL}/user/orders`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setOrders(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    axios
      .get(`${import.meta.env.VITE_API_URL}/addresses`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setAddresses(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  // Debounced address search
  useEffect(() => {
    if (addressDebounceTimer.current) {
      clearTimeout(addressDebounceTimer.current);
      addressDebounceTimer.current = null;
    }

    const trimmedQuery = addressQuery.trim();

    // Don't search if query is too short or same as last request
    if (trimmedQuery.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      lastRequestedQuery.current = "";
      return;
    }

    const isNewQuery = trimmedQuery !== lastRequestedQuery.current;

    if (isNewQuery && page === 4) {
      addressDebounceTimer.current = setTimeout(() => {
        const currentQuery = addressQuery.trim();
        const stillValid = currentQuery.length >= 3;
        const stillNew = currentQuery !== lastRequestedQuery.current;

        if (stillValid && stillNew && page === 4) {
          lastRequestedQuery.current = currentQuery;
          setIsLoadingSuggestions(true);
          setShowSuggestions(true);

          axios
            .get(
              `${
                import.meta.env.VITE_API_URL
              }/dadata/findAddress?query=${encodeURIComponent(currentQuery)}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
              }
            )
            .then((response) => {
              setAddressSuggestions(response.data || []);
              setIsLoadingSuggestions(false);
            })
            .catch((error) => {
              console.log(error);
              setAddressSuggestions([]);
              setIsLoadingSuggestions(false);
            });
        }
      }, 400); // 400ms debounce for better mobile UX
    }

    return () => {
      if (addressDebounceTimer.current) {
        clearTimeout(addressDebounceTimer.current);
      }
    };
  }, [addressQuery, page]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showSuggestions]);

  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    const selectedAddress = suggestion.unrestricted_value || suggestion.value;
    setAddressQuery(selectedAddress);
    setSelectedAddressSuggestion(suggestion);
    setAddressSuggestions([]);
    setShowSuggestions(false);
    lastRequestedQuery.current = selectedAddress;
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    // Handle address addition separately
    if (page === 4) {
      // Validate that address was selected from suggestions
      if (!selectedAddressSuggestion) {
        toast.error("Пожалуйста, выберите адрес из списка подсказок");
        return;
      }

      // Validate that current address matches the selected suggestion
      const currentAddress = addressQuery.trim();
      const selectedAddress =
        selectedAddressSuggestion.unrestricted_value ||
        selectedAddressSuggestion.value;

      if (currentAddress !== selectedAddress) {
        toast.error(
          "Адрес был изменен вручную. Пожалуйста, выберите адрес из списка подсказок"
        );
        return;
      }

      const addressData = {
        title: formData.get("title") || "",
        address: selectedAddress,
      };

      axios
        .post(`${import.meta.env.VITE_API_URL}/addresses`, addressData, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((_) => {
          toast.success("Адрес успешно добавлен");
          // Reset form state
          setAddressQuery("");
          setAddressSuggestions([]);
          setShowSuggestions(false);
          setSelectedAddressSuggestion(null);
          lastRequestedQuery.current = "";
          // Refresh addresses list
          axios
            .get(`${import.meta.env.VITE_API_URL}/addresses`, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              },
            })
            .then((response) => {
              setAddresses(response.data);
              setPage(3); // Go back to addresses list
            })
            .catch((error) => {
              console.log(error);
            });
        })
        .catch((_) => {
          toast.error("Введите название магазина!");
        });
      return;
    }

    // Handle user data update
    axios
      .post(`${import.meta.env.VITE_API_URL}/user`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((_) => {
        toast.success("Данные успешно обновлены");
        refreshUserData();
      })
      .catch((_) => {
        toast.error("Ошибка обновления данных");
      });
  };

  // Reset address state when switching pages
  useEffect(() => {
    if (page !== 4) {
      setAddressQuery("");
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setSelectedAddressSuggestion(null);
      lastRequestedQuery.current = "";
    }
  }, [page]);

  return (
    <MainContent header="Аккаунт">
      <SEO
        title="Управление аккаунтом"
        description="Управление аккаунтом"
        ogUrl="https://рыбныимир.рф/"
      />

      <div className={css.account}>
        <Tabs active={page} setActive={setPage} />

        {page === 0 && (
          <div className={css.col1}>
            <div className={css.orders}>
              {orders.map((item) => (
                <Order
                  id={item.id}
                  miniId={item.shortID}
                  createdAt={item.createdAt}
                  status={item.status}
                  totalPrice={item.totalPrice}
                  products={item.products}
                />
              ))}

              {orders.length <= 0 && (
                <a style={{ fontSize: 18 }}>заказов нет...</a>
              )}
            </div>
          </div>
        )}

        {page === 1 && (
          <div className={css.col1}>
            <div className={css.accountInfo}>
              <form className={css.form} onSubmit={handleSubmit}>
                <div className={css.flex}>
                  <b className={css.headAcc}>Личные данные</b>
                  <label>Имя</label>
                  <input
                    type="text"
                    name="firstName"
                    defaultValue={userData.firstName}
                    placeholder="Имя"
                  />
                  <label>Фамилия</label>
                  <input
                    type="text"
                    name="lastName"
                    defaultValue={userData.lastName}
                    placeholder="Фамилия"
                  />
                  <label>Имя пользователя</label>
                  <input
                    type="text"
                    name="username"
                    defaultValue={userData.username}
                    placeholder="Имя пользователя"
                  />

                  {userData.information?.inn && (
                    <div className={css.orgInfo}>
                      <div className={css.infoItem}>
                        <b>{userData.information?.name}</b>
                        <p>{userData.information?.address}</p>

                        <p style={{ marginTop: 15 }}>
                          ИНН: {userData.information?.inn}
                        </p>
                        <p>ОГРН: {userData.information?.ogrn}</p>
                      </div>

                      <div
                        className={`${css.statusInfo} ${
                          userData.information?.status === "ACTIVE"
                            ? css.active
                            : css.inactive
                        }`}
                      >
                        <span>
                          {userData.information?.status === "ACTIVE"
                            ? "Действующая"
                            : "Ликвидирована"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <button>Сохранить информацию</button>
                <a
                  onClick={logout}
                  style={{
                    color: "#000",
                    cursor: "pointer",
                    background: "#fff",
                    border: "1px solid #00000075",
                    padding: "10px",
                    borderRadius: 10,
                    textAlign: "center",
                    display: "block",
                    marginTop: 0,
                  }}
                >
                  Выйти из аккаунта
                </a>

                {!isSubscribed && !isApp && (
                  <button
                    type="button"
                    onClick={() => subscribe()}
                    disabled={isLoading}
                  >
                    <i
                      className="fi fi-rr-bell"
                      style={{
                        marginRight: 10,
                        top: 2.5,
                        position: "relative",
                      }}
                    />
                    Включить PUSH-уведомления
                  </button>
                )}
                {error && <span>{error}</span>}
              </form>
            </div>
          </div>
        )}

        {page === 2 && (
          <div className={css.col1}>
            <div className={css.accountInfo}>
              <b className={css.headAcc}>О нас</b>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginTop: 20,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #eee",
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <div
                    style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}
                  >
                    Рыбный мир
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr",
                      gap: 8,
                    }}
                  >
                    <span style={{ color: "#6b7280" }}>ИНН</span>
                    <span>550313913879</span>
                    <span style={{ color: "#6b7280" }}>ОГРНИП</span>
                    <span>325554300069246</span>
                    <span style={{ color: "#6b7280" }}>Адрес</span>
                    <span>ХМАО-Югра, г.Сургут, ул.Сосновая д.3</span>
                    <span style={{ color: "#6b7280" }}>Телефон</span>
                    <a
                      href="tel:+79227982617"
                      style={{ color: "#111827", textDecoration: "none" }}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = 'tel:+79227982617';
                      }}
                    >
                      +7 922 798-26-17
                    </a>
                    <span style={{ color: "#6b7280" }}>Карта</span>
                    <a
                      href="https://2gis.ru/surgut/geo/5489397701025000/73.483439,61.241362"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#2563eb" }}
                    >
                      Открыть в 2ГИС
                    </a>
                  </div>
                </div>

                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #eee",
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    id="map_348399838"
                    frameBorder="0"
                    width="100%"
                    height="300px"
                    src="https://makemap.2gis.ru/widget?data=eJw1jk1rwzAMhv-Ldg3FjvPhBnoYHRtlZZReBhs9hETrPJwoOCq0C_nvU5JOF4HeDz0DUKgxYP2C1CAHhz0UnwPwrUMo4BlLvgSECLpAHQae9QEq8hREf1Cx1V-x6OzYTwn1-nR06-1V7c20093btlL7n-P5cbMRW419FVzHjloxy-F319Z4hUKr_xkjOC8wt-nVneRArmXxVyTAri15Bs3NKrEmzdMo06s40cbqk-RdDYXJkvEUQVN2B-rd8nAAXzIUd2-WJsqY1OosAj_JS1tmYqvitaxc8IgagculVMDJ-_dvRP8xXzlccPwD3xZhLg"
                    sandbox="allow-modals allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        )}

        {page === 3 && (
          <div className={css.col1}>
            <div className={css.accountInfo}>
              <button
                type="button"
                style={{ width: "100%", marginBottom: 10 }}
                onClick={() => setPage(4)}
              >
                Добавить адрес
              </button>

              {addresses.map((item: any) => (
                <div key={item.id} className={css.addressItem}>
                  <h3>{item.title}</h3>
                  <p>{item.address}</p>
                </div>
              ))}

              {addresses.length <= 0 && (
                <a style={{ fontSize: 18 }}>адресов нет...</a>
              )}
            </div>
          </div>
        )}

        {page === 4 && (
          <div className={css.col1}>
            <div className={css.accountInfo}>
              <form className={css.form} onSubmit={handleSubmit}>
                <div className={css.flex}>
                  <b className={css.headAcc}>Добавить адрес</b>
                  <input
                    type="text"
                    name="title"
                    placeholder="Название магазина"
                  />
                  <div style={{ position: "relative", width: "100%" }}>
                    <input
                      ref={addressInputRef}
                      type="text"
                      name="address"
                      placeholder="Адрес"
                      value={addressQuery}
                      onChange={(e) => {
                        setAddressQuery(e.target.value);
                        // Reset selected suggestion if user manually edits the address
                        setSelectedAddressSuggestion(null);
                        if (e.target.value.trim().length >= 3) {
                          setShowSuggestions(true);
                        }
                      }}
                      onFocus={() => {
                        if (
                          addressQuery.trim().length >= 3 &&
                          addressSuggestions.length > 0
                        ) {
                          setShowSuggestions(true);
                        }
                      }}
                      autoComplete="off"
                      style={{ width: "100%" }}
                    />
                    {showSuggestions && (
                      <div
                        ref={suggestionsRef}
                        className={css.addressSuggestions}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          marginTop: "4px",
                          maxHeight: "300px",
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                        }}
                      >
                        {isLoadingSuggestions ? (
                          <div
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            Поиск...
                          </div>
                        ) : addressSuggestions.length > 0 ? (
                          addressSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              className={css.addressSuggestionItem}
                              onClick={(e) => {
                                e.preventDefault();
                                handleAddressSelect(suggestion);
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                handleAddressSelect(suggestion);
                              }}
                              style={{
                                padding: "14px 16px",
                                borderBottom: "1px solid #f3f4f6",
                                cursor: "pointer",
                                backgroundColor: "#fff",
                                transition: "background-color 0.15s",
                                fontSize: "16px",
                                lineHeight: "1.4",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#fff";
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 500,
                                  color: "#111827",
                                  marginBottom: "2px",
                                }}
                              >
                                {suggestion.value}
                              </div>
                              {suggestion.unrestricted_value &&
                                suggestion.unrestricted_value !==
                                  suggestion.value && (
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    {suggestion.unrestricted_value}
                                  </div>
                                )}
                            </div>
                          ))
                        ) : addressQuery.trim().length >= 3 ? (
                          <div
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            Адреса не найдены
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
                <button>Сохранить адрес</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainContent>
  );
}
